import React, {FC} from 'react';
import styled from 'styled-components';

import {composerDetailsTokenBoxStyle} from '../../../../libs/shared/ui/src/styles/composerStyles';
import {AddRecipients, RemoveRecipients, VisualRecipientMap} from '../../../helpers/models/recipientHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {DraftMessageModel} from '../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {RecipientsEditorContainer} from '../../common/editor/recipients/recipientsEditorContainer';
import {RecipientRoleFormatter} from '../../common/formatters/recipientRoleFormatter';
import {ComposerChannelPickerContainer} from './channelPicker/composerChannelPickerContainer';
import {ChannelSaver} from './channelPicker/pickChannelDropdownContainer';

/*
 * Props.
 */

export enum FocusModesEnum {
  TO = 'TO',
  CC = 'CC',
  BCC = 'BCC',
  SUBJECT = 'SUBJECT',
  BODY = 'BODY',
}

export interface ComposerDetailsPublicProps {
  channel: ChannelModel;
  message: DraftMessageModel;
  namespace: string;
  recipients: VisualRecipientMap;
  addRecipients: AddRecipients;
  removeRecipients: RemoveRecipients;
  saveChannel: ChannelSaver;
  focusMode?: FocusModesEnum;
  focusKey?: unknown;
  className?: string;
}

interface ComposerDetailsProps extends ComposerDetailsPublicProps {
  isCcVisible: boolean;
  isBccVisible: boolean;
}

/*
 * Style.
 */

const StyledRecipientsDiv = styled.div`
  grid-area: recipients;
  display: flex;
  flex-direction: column;
  padding: 0px 0px 10px 16px;
  overflow: hidden;
`;

const StyledSectionDiv = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const StyledSectionTitleDiv = styled.div`
  flex-shrink: 0;
  ${composerDetailsTokenBoxStyle};
  color: ${(p) => p.theme.greys.shade70};
  margin-right: 6px;
`;

const StyledEditorDiv = styled.div`
  flex: 1;
  overflow: hidden;
`;

/*
 * Component.
 */

export const ComposerDetails: FC<ComposerDetailsProps> = (props) => (
  <StyledRecipientsDiv className={props.className}>
    <StyledSectionDiv>
      <StyledSectionTitleDiv>
        <RecipientRoleFormatter role={RecipientRolesEnum.FROM} />:
      </StyledSectionTitleDiv>
      <ComposerChannelPickerContainer message={props.message} saveChannel={props.saveChannel} />
    </StyledSectionDiv>
    {renderRecipientBlock(props, RecipientRolesEnum.TO)}
    {props.isCcVisible && renderRecipientBlock(props, RecipientRolesEnum.CC)}
    {props.isBccVisible && renderRecipientBlock(props, RecipientRolesEnum.BCC)}
  </StyledRecipientsDiv>
);

function renderRecipientBlock(props: ComposerDetailsProps, role: RecipientRolesEnum) {
  // Render the editor in a section, with a title.
  const roleRecipients = props.recipients[role];
  const shouldFocus =
    (role === RecipientRolesEnum.TO && props.focusMode === FocusModesEnum.TO) ||
    (role === RecipientRolesEnum.CC && props.focusMode === FocusModesEnum.CC) ||
    (role === RecipientRolesEnum.BCC && props.focusMode === FocusModesEnum.BCC);

  return (
    <StyledSectionDiv>
      <StyledSectionTitleDiv>
        <RecipientRoleFormatter role={role} />:
      </StyledSectionTitleDiv>
      <StyledEditorDiv>
        <RecipientsEditorContainer
          shouldFocus={shouldFocus}
          focusKey={props.focusKey}
          source={props.channel.contactSource}
          namespace={props.namespace}
          role={role}
          recipients={roleRecipients || []}
          addRecipients={props.addRecipients}
          removeRecipients={props.removeRecipients}
        />
      </StyledEditorDiv>
    </StyledSectionDiv>
  );
}
